<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '@/api/api.js'
import HeroBlock from '@/components/presentation/HeroBlock.vue'
import DropdownGallery from '@/components/DropdownGallery.vue'
import InfoCard from '@/components/InfoCard.vue'
import { showToast } from '@/utils/toast'
import type { CommercialData, Project } from '@/types/api'

const commercialData = ref<CommercialData | null>(null)
const blocks = ref<NonNullable<CommercialData['blocks']> | []>([])
const loading = ref(true)
const route = useRoute()

async function loadCommercial() {
  loading.value = true
  try {
    const userName = route.params.name as string | undefined
    commercialData.value = userName
      ? await api.getCommercialByUser(userName)
      : await api.getKpPageCommercial()
    if (commercialData.value?.blocks) {
      blocks.value = [...commercialData.value.blocks].sort((a, b) => a.position - b.position)
    } else {
      blocks.value = []
    }
  } catch (error) {
    console.error('Error loading commercial data:', error)
  } finally {
    loading.value = false
  }
}

// Один источник загрузки: при монтировании и при смене автора (watch с immediate)
watch(
  () => route.params.name,
  () => loadCommercial(),
  { immediate: true },
)

const transformProjectsToVideos = (projects: Project[] = []) => {
  return projects
    .filter((project) => project.cover_url)
    .map((project) => ({
      videoSrc: project.short_video_url || '/static/placeholders/placeholder-video.webp',
      posterSrc: project.cover_url || '/static/placeholders/placeholder-image.webp',
      title: project.title || '',
    }))
}

const downloadFile = async (url?: string) => {
  if (!url) return
  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    let filename = ''
    const cd = res.headers.get('Content-Disposition') || res.headers.get('content-disposition')
    if (cd) {
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i)
      if (match && match[1]) {
        filename = decodeURIComponent(match[1].trim().replace(/^['"]|['"]$/g, ''))
      }
    }
    if (!filename) {
      filename = url.split('/').pop() || 'file'
    }

    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    a.click()
    URL.revokeObjectURL(blobUrl)
    showToast('Файл успешно загружен')
  } catch {
    // В fallback-режиме браузер сам использует имя файла с API
    window.open(url, '_blank')
    showToast('Файл успешно загружен')
  }
}

const downloadFrameContract = () => {
  if (!commercialData.value) return
  downloadFile(commercialData.value.frame_contract_url)
}

const downloadCommercialOffer = () => {
  if (!commercialData.value) return
  downloadFile(commercialData.value.commercial_offer_url)
}
</script>

<template>
  <div class="commercial-page">
    <div class="commercial-container-hero">
      <HeroBlock
        type="hero-img-text-left"
        image="/static/kp.png"
        title="Медиа-продакшн полного цикла"
        buttonText=""
        fallback="/static/placeholders/placeholder-image.webp"
      />
    </div>
    <div class="commercial-content">
      <div class="commercial-container-faq">
        <template v-if="loading">
          <div v-for="n in 3" :key="n" class="commercial-faq-skeleton">
            <div class="commercial-faq-skeleton-title"></div>
            <div class="commercial-faq-skeleton-line"></div>
            <div class="commercial-faq-skeleton-line commercial-faq-skeleton-line--short"></div>
          </div>
        </template>
        <template v-else>
          <DropdownGallery
            v-for="block in blocks"
            :key="block.position"
            :title="block.text"
            :subtitle="block.description"
            :videos="transformProjectsToVideos(block.projects || [])"
          />
        </template>
      </div>
      <div class="commercial-container-info">
        <h2 class="commercial-info-cards-title">ПЛЮСЫ РАБОТЫ ПО РАМОЧНОМУ ДОГОВОРУ</h2>
        <div class="commercial-container-info-cards">
          <InfoCard number="#1" title="Единый Подрядчик" subtitle="По всем видам услуг" />
          <InfoCard number="#2" title="Подписываем один раз в год" subtitle="" />
          <InfoCard
            number="#3"
            title="Под каждую задачу формируем только ТЗ"
            subtitle="Сокращается время оформления"
          />
        </div>
        <div class="commercial-container">
          <div class="commercial-contract-block">
            <img class="commercial-contract-icon" src="/static/icons/document.svg" alt="Документ" />
            <div class="commercial-contract-text">
              <div class="commercial-contract-title">РАМОЧНЫЙ ДОГОВОР ПРОЕКТА</div>
              <div class="commercial-contract-subtitle" @click="downloadFrameContract">Скачать</div>
            </div>
          </div>
          <div class="commercial-contract-block">
            <img class="commercial-contract-icon" src="/static/icons/document.svg" alt="Документ" />
            <div class="commercial-contract-text">
              <div class="commercial-contract-title">Коммерческое предложение</div>
              <div class="commercial-contract-subtitle" @click="downloadCommercialOffer">
                Скачать
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.commercial-page {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.commercial-content {
  max-width: 1524px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.commercial-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 2rem 0;
}

@media (min-width: 769px) and (max-width: 1024px) {
  .commercial-container {
    gap: 1.5rem;
    padding: 1.5rem 0;
  }

  .commercial-contract-block {
    flex: 1 1 calc(50% - 0.75rem);
  }
}

@media (min-width: 1025px) and (max-width: 1280px) {
  .commercial-container {
    gap: 1.75rem;
  }
}

@media (max-width: 768px) {
  .commercial-container {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 1.5rem 0;
  }
}

.commercial-container-faq {
  display: flex;
  flex-direction: column;
  gap: 0rem;
  padding: 0 4rem;
}

.commercial-faq-skeleton {
  padding: 1.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.commercial-faq-skeleton-title {
  width: 40%;
  height: 1.25rem;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: commercial-skeleton-shimmer 1.4s ease-in-out infinite;
  margin-bottom: 0.75rem;
}

.commercial-faq-skeleton-line {
  width: 100%;
  height: 0.75rem;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: commercial-skeleton-shimmer 1.4s ease-in-out infinite;
  margin-bottom: 0.5rem;
}

.commercial-faq-skeleton-line--short {
  width: 70%;
}

@keyframes commercial-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 64rem) {
  .commercial-container-faq {
    padding: 0 2.5rem;
  }
}

@media (max-width: 52.5rem) {
  .commercial-container-faq {
    padding: 0 2.25rem;
  }
}

.commercial-container-hero {
  margin-bottom: 2rem;
}

.commercial-container-hero :deep(.hero-block) {
  height: 70vh;
}

@media (min-width: 769px) and (max-width: 1024px) {
  .commercial-container-hero :deep(.hero-block) {
    height: 60vh;
  }
}

@media (min-width: 1025px) and (max-width: 1280px) {
  .commercial-container-hero :deep(.hero-block) {
    height: 65vh;
  }
}

.commercial-container-hero :deep(.hero-content) {
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  text-align: center;
}

.commercial-container-hero :deep(.hero-text-wrapper) {
  text-align: center;
  width: 100%;
}

.commercial-container-hero :deep(.hero-title) {
  color: #fff;
  text-align: center;
  text-transform: uppercase;
}

.commercial-container-hero :deep(.hero-gradient-overlay) {
  display: none;
}

.commercial-container-info {
  max-width: 90rem;
  margin: 0;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 4rem;
}

@media (max-width: 64rem) {
  .commercial-container-info {
    padding: 0 2.5rem;
  }
}

@media (max-width: 52.5rem) {
  .commercial-container-info {
    padding: 0 2.25rem;
  }
}

@media (min-width: 1281px) {
  .commercial-container-info {
    padding: 0 4rem;
  }
}

.commercial-info-cards-title {
  font-size: 2rem;
  font-weight: bold;
  text-align: left;
  margin-bottom: 0;
  width: 100%;
}

@media (min-width: 769px) and (max-width: 1024px) {
  .commercial-info-cards-title {
    font-size: 1.75rem;
  }
}

@media (min-width: 1025px) and (max-width: 1280px) {
  .commercial-info-cards-title {
    font-size: 1.875rem;
  }
}

.commercial-container-info-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 2.5rem;
  justify-content: flex-start;
  width: 100%;
}

@media (min-width: 769px) and (max-width: 1024px) {
  .commercial-container-info-cards {
    gap: 2rem;
  }

  .commercial-container-info-cards :deep(.info-card) {
    flex: 1 1 calc(50% - 1rem);
    min-width: calc(50% - 1rem);
  }
}

@media (min-width: 1025px) and (max-width: 1280px) {
  .commercial-container-info-cards {
    gap: 2.25rem;
  }

  .commercial-container-info-cards :deep(.info-card) {
    flex: 1 1 calc(33.333% - 1.5rem);
    min-width: calc(33.333% - 1.5rem);
  }
}

.commercial-contract-block {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
}

.commercial-contract-icon {
  width: 2.5rem;
  height: 2.5rem;
}

.commercial-contract-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.commercial-contract-title {
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
}

.commercial-contract-subtitle {
  font-size: 0.9rem;
  color: #555;
  cursor: pointer;
  text-decoration: underline;
}

@media (max-width: 48rem) {
  .commercial-container-info {
    padding: 0 1.25rem;
    gap: 1.25rem;
  }

  .commercial-container-faq {
    padding: 0 1.25rem;
    gap: 1.5rem;
  }

  .commercial-info-cards-title {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  .commercial-container-info-cards {
    gap: 1.25rem;
  }

  .commercial-container-info-cards :deep(.info-card) {
    flex: 1 1 100%;
    width: 100%;
  }

  .commercial-contract-block {
    margin-bottom: 1.5rem;
  }
}
</style>
